import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  MEAL_FACTOR_MAX,
  MEAL_FACTOR_MIN,
  NO_DEVIATION,
  clampFactor,
  deviationId,
  householdExtraLogId,
  householdLogId,
  isWorkoutMeal,
  mealRules,
  mealRulesId,
  readDeviation,
} from '@kraftwerk/shared';
import type { DaySlot, MealDeviation, MealExtra, MealRules, Serving } from '@kraftwerk/shared';
import { db, ignoreClosed } from '../../db/local.js';
import type {
  LocalFood,
  LocalFoodLogEntry,
  LocalHouseholdMeal,
  LocalMealDeviation,
  LocalRecipe,
} from '../../db/local.js';
import {
  dishFromRecipe,
  extraFromFood,
  extraFromRecipe,
  myServing,
  saveDeviation,
  saveMealRules,
  syncHouseholdMeals,
} from '../../db/householdMeals.js';
import type { MealContext } from '../../db/householdMeals.js';
import { listFoods, listRecipes } from '../../db/nutrition.js';
import { currentHouseholdId } from '../../db/shopping.js';
import { toNumber } from '../nutrition/FoodForm.js';
import { useMealContext } from './HouseholdMeals.js';

/** Ein Haushaltseintrag in meinem Tag — mit meiner Abweichung. */
export interface HouseholdItem {
  entry: LocalHouseholdMeal;
  deviation: MealDeviation | null;
  serving: Serving;
}

/**
 * Die Haushaltseinträge eines Tages für mich — und der Abgleich meiner
 * Protokollzeilen (Geplant zählt als gegessen, ab heute). Liest flach:
 * Haushalt und Kontext werden hineingereicht.
 */
export function useHouseholdDay(
  date: string,
  trainingToday: boolean,
): {
  items: HouseholdItem[];
  context: MealContext | null;
  rules: MealRules;
  householdId: string | null;
} {
  const context = useMealContext();
  const householdId = useLiveQuery(() => currentHouseholdId(db), [], null);
  const entries = useLiveQuery(
    () =>
      householdId
        ? db.household_meals.where('householdId').equals(householdId).toArray()
        : Promise.resolve([] as LocalHouseholdMeal[]),
    [householdId],
    [] as LocalHouseholdMeal[],
  );
  const deviations = useLiveQuery(
    () => db.meal_deviations.toArray(),
    [],
    [] as LocalMealDeviation[],
  );
  const me = context?.me ?? '';
  const rules = mealRules(
    deviations.find((row) => row.id === mealRulesId(me) && !row.deletedAt)?.rules,
  );
  const items = entries
    .filter((entry) => !entry.deletedAt && entry.status === 'planned' && entry.date === date)
    .flatMap((entry): HouseholdItem[] => {
      const row = deviations.find((candidate) => candidate.id === deviationId(me, entry.id));
      const deviation = row && !row.deletedAt ? readDeviation(row) : null;
      const serving = myServing(entry, deviation, rules);
      return serving ? [{ entry, deviation, serving }] : [];
    });

  const key = entries
    .filter((entry) => entry.date === date)
    .map((entry) => `${entry.id}:${entry.updatedAt}`)
    .join();
  const rulesKey = JSON.stringify(rules);
  useEffect(() => {
    if (!context) return;
    syncHouseholdMeals(householdId, context, db, {
      training: { dates: [date], on: new Set(trainingToday ? [date] : []) },
    }).catch(ignoreClosed);
    // `context` ist je Darstellung neu — es zählen seine Werte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    householdId,
    date,
    key,
    rulesKey,
    trainingToday,
    context?.me,
    context?.today,
    context?.nutritionOn,
  ]);

  return { items, context, rules, householdId };
}

/** Kennungen der Protokollzeilen, die zu Haushaltseinträgen gehören. */
export function householdRowIds(items: readonly HouseholdItem[], me: string): Set<string> {
  const ids = new Set<string>();
  for (const { entry, serving } of items) {
    ids.add(householdLogId(me, entry.id));
    for (const extra of serving.extras) ids.add(householdExtraLogId(me, entry.id, extra.id));
  }
  return ids;
}

type Panel = 'portion' | 'extra' | 'variant' | null;

/**
 * Ein Haushaltseintrag in meinem Tag: zählt nach „Geplant zählt als
 * gegessen“, Abweichungen direkt am Eintrag. Die Nährwerte bleiben bei mir;
 * der Haushalt erfährt nur Portion und was zu kaufen ist.
 */
export function HouseholdRow({
  item,
  rows,
  context,
  vegetarian,
}: {
  item: HouseholdItem;
  /** Alle Protokollzeilen des Tages, auch weich gelöschte. */
  rows: readonly LocalFoodLogEntry[];
  context: MealContext;
  vegetarian: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { entry, serving } = item;
  const own = item.deviation ?? NO_DEVIATION;
  const [panel, setPanel] = useState<Panel>(null);
  const main = rows.find((row) => row.id === householdLogId(context.me, entry.id));
  const counted = main && !main.deletedAt ? main : null;
  const name = serving.dish.name;
  const save = (next: MealDeviation) => {
    setPanel(null);
    void saveDeviation(entry, next, context, db);
  };
  const toggle = (next: Panel) => setPanel((value) => (value === next ? null : next));

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-line border-l-4 border-l-good px-3 py-2 text-sm">
      <div className="min-w-0">
        <span className="kw-kicker block">{t('householdMeals.day.kicker')}</span>
        <span
          className={`block font-semibold break-words ${serving.eats ? '' : 'text-ink-muted line-through'}`}
        >
          {name}
        </span>
        {serving.variant ? (
          <span className="block text-xs text-ink-muted">
            {t('householdMeals.day.instead', { name: entry.dish.name })}
          </span>
        ) : null}
        <span className="block text-xs text-ink-muted">
          {!serving.eats
            ? t('householdMeals.day.notEating')
            : counted
              ? t('householdMeals.day.counted', {
                  amount: serving.portions.toLocaleString(i18n.language),
                  kcal: Math.round(counted.kcal),
                  protein: Math.round(counted.protein),
                })
              : serving.dish.perPortion
                ? t('householdMeals.day.counted', {
                    amount: serving.portions.toLocaleString(i18n.language),
                    kcal: Math.round(serving.dish.perPortion.kcal * serving.portions),
                    protein: Math.round(serving.dish.perPortion.protein * serving.portions),
                  })
                : t('householdMeals.day.noValues')}
          {serving.eats && serving.factorFrom === 'rule'
            ? ` · ${t('householdMeals.day.fromRule')}`
            : ''}
        </span>
        {vegetarian && serving.eats && !serving.variant ? (
          <span className="block text-xs text-warn">{t('householdMeals.day.vegetarianHint')}</span>
        ) : null}
      </div>

      {serving.extras.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {serving.extras.map((extra) => (
            <li key={extra.id} className="flex items-center justify-between gap-2">
              <span className="min-w-0 break-words">
                + {extra.name}
                <span className="text-xs text-ink-muted">
                  {' · '}
                  {extraLine(extra, t, i18n.language)}
                </span>
              </span>
              <button
                type="button"
                onClick={() =>
                  save({
                    ...own,
                    extras: own.extras.filter((candidate) => candidate.id !== extra.id),
                  })
                }
                aria-label={t('householdMeals.day.removeExtraNamed', { name: extra.name })}
                className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 hover:border-danger hover:text-danger"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => save({ ...own, skip: !own.skip })}
          aria-label={
            own.skip
              ? t('householdMeals.day.eatAgainNamed', { name })
              : t('householdMeals.day.skipNamed', { name })
          }
          className="kw-btn kw-btn-quiet kw-btn-small px-3"
        >
          {own.skip ? t('householdMeals.day.eatAgain') : t('householdMeals.day.skip')}
        </button>
        {serving.eats ? (
          <>
            <button
              type="button"
              onClick={() => toggle('portion')}
              aria-expanded={panel === 'portion'}
              aria-label={t('householdMeals.day.portionNamed', { name })}
              className="kw-btn kw-btn-quiet kw-btn-small px-3"
            >
              {t('householdMeals.day.portion')}
            </button>
            <button
              type="button"
              onClick={() => toggle('extra')}
              aria-expanded={panel === 'extra'}
              aria-label={t('householdMeals.day.extraNamed', { name })}
              className="kw-btn kw-btn-quiet kw-btn-small px-3"
            >
              {t('householdMeals.day.extra')}
            </button>
            {own.variant ? (
              <button
                type="button"
                onClick={() => save({ ...own, variant: null })}
                className="kw-btn kw-btn-quiet kw-btn-small px-3"
              >
                {t('householdMeals.day.variantRemove')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => toggle('variant')}
                aria-expanded={panel === 'variant'}
                aria-label={t('householdMeals.day.variantNamed', { name: entry.dish.name })}
                className="kw-btn kw-btn-quiet kw-btn-small px-3"
              >
                {t('householdMeals.day.variant')}
              </button>
            )}
          </>
        ) : null}
      </div>

      {panel === 'portion' ? (
        <PortionForm
          current={serving.portions}
          onSave={(factor) => save({ ...own, factor })}
          onReset={() => save({ ...own, factor: null })}
        />
      ) : null}
      {panel === 'extra' ? (
        <ExtraForm onAdd={(extra) => save({ ...own, extras: [...own.extras, extra] })} />
      ) : null}
      {panel === 'variant' ? (
        <VariantForm
          onPick={(recipe) =>
            void dishFromRecipe(recipe, db).then((variant) => save({ ...own, variant }))
          }
        />
      ) : null}
    </li>
  );
}

function PortionForm({
  current,
  onSave,
  onReset,
}: {
  current: number;
  onSave: (factor: number) => void;
  onReset: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [value, setValue] = useState(current.toLocaleString(i18n.language));
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        const factor = toNumber(value);
        if (factor === null || factor < MEAL_FACTOR_MIN || factor > MEAL_FACTOR_MAX) return;
        onSave(clampFactor(factor));
      }}
    >
      <label htmlFor={id} className="flex w-32 flex-col gap-1 text-xs text-ink-muted">
        {t('householdMeals.day.portionLabel')}
        <input
          id={id}
          inputMode="decimal"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          className="kw-field text-ink"
        />
      </label>
      <button type="submit" className="kw-btn kw-btn-small px-4">
        {t('householdMeals.day.portionSave')}
      </button>
      <button type="button" onClick={onReset} className="kw-btn kw-btn-quiet kw-btn-small px-4">
        {t('householdMeals.day.portionReset')}
      </button>
    </form>
  );
}

/**
 * Ein Extra aus einem **eigenen** Lebensmittel (Gramm) oder Gericht
 * (Portionen) — mit dessen Werten und Quelle, nie erfunden. Ein Gericht ohne
 * Nährwerte geht nicht, statt still mit 0 zu zählen.
 */
export function ExtraForm({ onAdd }: { onAdd: (extra: MealExtra) => void }): React.JSX.Element {
  const { t } = useTranslation();
  const ids = { pick: useId(), amount: useId() };
  const foods = useLiveQuery(() => listFoods(db), [], [] as LocalFood[]);
  const recipes = useLiveQuery(() => listRecipes(db), [], [] as LocalRecipe[]);
  const [pick, setPick] = useState('');
  const [amount, setAmount] = useState('');
  const [problem, setProblem] = useState(false);
  const isRecipe = pick.startsWith('r:');
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        setProblem(false);
        const value = toNumber(amount || (isRecipe ? '1' : ''));
        if (!pick || value === null || !(value > 0)) return;
        const id = pick.slice(2);
        if (isRecipe) {
          const recipe = recipes.find((entry) => entry.id === id);
          if (!recipe) return;
          void extraFromRecipe(recipe, value, db).then((extra) => {
            if (extra) onAdd(extra);
            else setProblem(true);
          });
          return;
        }
        const food = foods.find((entry) => entry.id === id);
        if (food) onAdd(extraFromFood(food, value));
      }}
    >
      <label
        htmlFor={ids.pick}
        className="flex min-w-40 flex-1 flex-col gap-1 text-xs text-ink-muted"
      >
        {t('householdMeals.day.pickFood')}
        <select
          id={ids.pick}
          value={pick}
          onChange={(event) => setPick(event.target.value)}
          className="kw-field text-ink"
        >
          <option value="">{t('householdMeals.day.choose')}</option>
          <optgroup label={t('householdMeals.day.pickFood')}>
            {foods.map((food) => (
              <option key={food.id} value={`f:${food.id}`}>
                {food.name}
              </option>
            ))}
          </optgroup>
          <optgroup label={t('householdMeals.day.pickRecipe')}>
            {recipes.map((recipe) => (
              <option key={recipe.id} value={`r:${recipe.id}`}>
                {recipe.name}
              </option>
            ))}
          </optgroup>
        </select>
      </label>
      <label htmlFor={ids.amount} className="flex w-28 flex-col gap-1 text-xs text-ink-muted">
        {isRecipe ? t('householdMeals.day.portions') : t('householdMeals.day.grams')}
        <input
          id={ids.amount}
          inputMode="decimal"
          value={amount}
          placeholder={isRecipe ? '1' : ''}
          onChange={(event) => setAmount(event.target.value)}
          className="kw-field text-ink"
        />
      </label>
      <button type="submit" className="kw-btn kw-btn-small px-4">
        {t('householdMeals.day.add')}
      </button>
      {problem ? (
        <p role="alert" className="w-full text-xs text-warn">
          {t('householdMeals.day.noRecipeValues')}
        </p>
      ) : null}
    </form>
  );
}

function VariantForm({ onPick }: { onPick: (recipe: LocalRecipe) => void }): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const recipes = useLiveQuery(() => listRecipes(db), [], [] as LocalRecipe[]);
  const [pick, setPick] = useState('');
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        const recipe = recipes.find((entry) => entry.id === pick);
        if (recipe) onPick(recipe);
      }}
    >
      <label htmlFor={id} className="flex min-w-40 flex-1 flex-col gap-1 text-xs text-ink-muted">
        {t('householdMeals.day.pickRecipe')}
        <select
          id={id}
          value={pick}
          onChange={(event) => setPick(event.target.value)}
          className="kw-field text-ink"
        >
          <option value="">{t('householdMeals.day.choose')}</option>
          {recipes.map((recipe) => (
            <option key={recipe.id} value={recipe.id}>
              {recipe.name}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" className="kw-btn kw-btn-small px-4">
        {t('householdMeals.day.add')}
      </button>
    </form>
  );
}

const RULE_FACTORS = [0.5, 0.75, 1.25, 1.5, 2] as const;

/**
 * Dauerregeln fürs Mitessen: Portion je Mahlzeit, Extra an Trainingstagen
 * (Post-Workout), „vegetarisch“ als Hinweis an den Haushalt.
 */
export function MealRulesSetting({
  slots,
  householdId,
  context,
  rules,
}: {
  slots: readonly DaySlot[];
  householdId: string | null;
  context: MealContext;
  rules: MealRules;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState<MealRules>(rules);
  const [saved, setSaved] = useState(false);
  const own = slots.filter((slot) => !isWorkoutMeal(slot));

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-ink-muted">{t('householdMeals.rules.intro')}</p>
      {own.map((slot) => (
        <FactorSelect
          key={slot}
          label={t('householdMeals.rules.factor', { meal: t(`nutrition.slot.${slot}`) })}
          value={draft.factors[slot] ?? 1}
          onChange={(factor) => {
            setSaved(false);
            const factors = { ...draft.factors };
            if (factor === 1) delete factors[slot];
            else factors[slot] = factor;
            setDraft({ ...draft, factors });
          }}
        />
      ))}
      <div className="flex flex-col gap-1">
        <p className="text-xs text-ink-muted">{t('householdMeals.rules.trainingExtra')}</p>
        {draft.trainingExtra ? (
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 break-words">
              {draft.trainingExtra.name} · {extraLine(draft.trainingExtra, t, i18n.language)}
            </span>
            <button
              type="button"
              onClick={() => {
                setSaved(false);
                setDraft({ ...draft, trainingExtra: null });
              }}
              aria-label={t('householdMeals.day.removeExtraNamed', {
                name: draft.trainingExtra.name,
              })}
              className="kw-btn kw-btn-quiet kw-btn-small px-3"
            >
              ×
            </button>
          </div>
        ) : (
          <ExtraForm
            onAdd={(extra) => {
              setSaved(false);
              setDraft({ ...draft, trainingExtra: extra });
            }}
          />
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="kw-toggle"
          aria-pressed={draft.vegetarian}
          onClick={() => {
            setSaved(false);
            setDraft({ ...draft, vegetarian: !draft.vegetarian });
          }}
        >
          {t('householdMeals.rules.vegetarian')}
        </button>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="kw-btn kw-btn-small px-4"
          onClick={() =>
            void saveMealRules(householdId, draft, context, db).then(() => setSaved(true))
          }
        >
          {t('householdMeals.rules.save')}
        </button>
        {saved ? (
          <p role="status" className="text-xs text-good">
            {t('householdMeals.rules.saved')}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function FactorSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (factor: number) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const id = useId();
  const options = [1, ...RULE_FACTORS].sort((a, b) => a - b);
  return (
    <label htmlFor={id} className="flex flex-col gap-1 text-xs text-ink-muted">
      {label}
      <select
        id={id}
        value={String(value)}
        onChange={(event) => onChange(Number(event.target.value))}
        className="kw-field text-ink"
      >
        {options.map((factor) => (
          <option key={factor} value={String(factor)}>
            {factor === 1
              ? t('householdMeals.rules.sameAsAll')
              : `${factor.toLocaleString(i18n.language)}×`}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Ein Extra in einer Zeile: Menge, Kalorien, Eiweiß. Vorher stand bei der
 * Dauerregel nur „· 33 g“ — das Eiweiß, gelesen als Menge (250 g Haferflocken
 * sahen aus wie 33 g; Geräteabnahme 28.10.2026).
 */
export function extraLine(extra: MealExtra, t: TFunction, language: string): string {
  const amount =
    extra.grams !== null
      ? t('householdMeals.day.extraGrams', { grams: extra.grams.toLocaleString(language) })
      : extra.portions !== null
        ? t('householdMeals.day.extraPortions', {
            count: extra.portions,
            portions: extra.portions.toLocaleString(language),
          })
        : null;
  const values = t('householdMeals.day.extraValues', {
    kcal: Math.round(extra.macros.kcal),
    protein: Math.round(extra.macros.protein),
  });
  return amount ? `${amount} · ${values}` : values;
}
